import { Record } from "immutable";
import PublishStatus from "models/enumerations/publish-status";
import type Solution from "models/interfaces/situational-navigation/solutions/solution";
import PublishableUtils, {
    PublishableRecord,
} from "utilities/publishable-utils";
import { RouteUtils } from "utilities/route-utils";
import { siteMap } from "internal-sitemap";
import { RecordUtils } from "@rsm-hcd/javascript-core";

const defaultValues: Solution =
    RecordUtils.auditableDefaultValuesFactory<Solution>({
        body: "",
        bodyDraft: "",
        deletedDraftById: undefined,
        deletedDraftOn: undefined,
        publishedById: undefined,
        publishedOn: undefined,
        subtitle: "",
        subtitleDraft: "",
        title: "",
        titleDraft: "",
    });

export default class SolutionRecord
    extends Record(defaultValues)
    implements Solution, PublishableRecord
{
    // -----------------------------------------------------------------------------------------
    // #region Properties
    // -----------------------------------------------------------------------------------------

    // Do NOT set properties on immutable records due to babel and typescript transpilation issue
    // See https://github.com/facebook/create-react-app/issues/6506

    // #endregion Properties

    // -----------------------------------------------------------------------------------------
    // #region Constructor
    // -----------------------------------------------------------------------------------------

    constructor(params?: Solution) {
        if (params == null) {
            params = Object.assign({}, defaultValues);
        }

        super(params);
    }

    // #endregion Constructor

    // -----------------------------------------------------------------------------------------
    // #region Public Methods
    // -----------------------------------------------------------------------------------------

    /**
     * Returns the route to access non-admin solution page
     */
    public getRoute(): string {
        return RouteUtils.getUrl(siteMap.situationalNavigation.solutions, {
            id: this.id,
        });
    }

    public hasUnpublishedChanges(): boolean {
        return PublishableUtils.hasUnpublishedChanges(this);
    }

    public getPublishStatus(): PublishStatus {
        return PublishableUtils.getPublishStatus(this);
    }

    /**
     * Merges new values into the record and returns a new instance.
     *
     * @param {Partial<Solution>} values
     */
    public with(values: Partial<Solution>): SolutionRecord {
        return new SolutionRecord(Object.assign(this.toJS(), values));
    }

    // #endregion Public Methods
}
